import { useRef, useMemo, Suspense } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { OBJLoader, MeshSurfaceSampler } from 'three-stdlib';
import * as THREE from 'three';
import objUrl from '../assets/objects/stimmdigital_3d.obj?url';

const PRIMARY_COLOR = '#77F76C';

interface Waypoint {
    position: THREE.Vector3;
    neighbors: number[];
}

interface Particle {
    currentWaypoint: number;
    targetWaypoint: number;
    progress: number;
    speed: number;
    dx: number;
    dy: number;
    dz: number;
}

const ParticleObject = () => {
  const obj = useLoader(OBJLoader, objUrl);
  const pointsRef = useRef<THREE.Points>(null);
  const geometryRef = useRef<THREE.BufferGeometry>(null);

  // 1. Generate Waypoints on the surface using MeshSurfaceSampler
  // 2. Build a graph connecting nearby waypoints
  // 3. Initialize particles on these waypoints
  const { positions, particles, waypoints, sizes } = useMemo(() => {
    const generatedWaypoints: THREE.Vector3[] = [];
    const meshes: THREE.Mesh[] = [];
    
    // Collect meshes
    obj.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
            meshes.push(child as THREE.Mesh);
        }
    });

    if (meshes.length === 0) return { positions: new Float32Array(0), particles: [], waypoints: [], sizes: new Float32Array(0) };

    // Calculate total area for proportional sampling
    let totalArea = 0;
    const samplers = meshes.map(mesh => {
        const sampler = new MeshSurfaceSampler(mesh);
        try {
            sampler.build();
        } catch (e) {
            return null;
        }
        
        let area = 0;
        const geom = mesh.geometry;
        const pos = geom.attributes.position;
        const index = geom.index;
        const p1 = new THREE.Vector3(), p2 = new THREE.Vector3(), p3 = new THREE.Vector3();
        
        if (index) {
            for (let i = 0; i < index.count; i += 3) {
                p1.fromBufferAttribute(pos, index.getX(i));
                p2.fromBufferAttribute(pos, index.getX(i+1));
                p3.fromBufferAttribute(pos, index.getX(i+2));
                const cross = new THREE.Vector3().crossVectors(
                    new THREE.Vector3().subVectors(p2, p1),
                    new THREE.Vector3().subVectors(p3, p1)
                );
                area += cross.length() * 0.5;
            }
        } else {
            for (let i = 0; i < pos.count; i += 3) {
                p1.fromBufferAttribute(pos, i);
                p2.fromBufferAttribute(pos, i+1);
                p3.fromBufferAttribute(pos, i+2);
                const cross = new THREE.Vector3().crossVectors(
                    new THREE.Vector3().subVectors(p2, p1),
                    new THREE.Vector3().subVectors(p3, p1)
                );
                area += cross.length() * 0.5;
            }
        }
        
        totalArea += area;
        return { sampler, mesh, area };
    }).filter(s => s !== null) as { sampler: MeshSurfaceSampler, mesh: THREE.Mesh, area: number }[];

    // Sample Points
    const WAYPOINT_COUNT = 1500; 
    const tempPos = new THREE.Vector3();
    
    samplers.forEach(({ sampler, mesh, area }) => {
        const count = Math.floor(WAYPOINT_COUNT * (area / totalArea));
        mesh.updateMatrix();
        
        for (let i = 0; i < count; i++) {
            sampler.sample(tempPos);
            tempPos.applyMatrix4(mesh.matrix);
            generatedWaypoints.push(tempPos.clone());
        }
    });

    // Normalization
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    generatedWaypoints.forEach(p => {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
        if (p.z < minZ) minZ = p.z;
        if (p.z > maxZ) maxZ = p.z;
    });

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
    
    const sizeX = maxX - minX;
    const sizeY = maxY - minY;
    const sizeZ = maxZ - minZ;
    const maxDim = Math.max(sizeX, sizeY, sizeZ);
    
    const targetSize = 4.0;
    const scale = targetSize / maxDim;

    generatedWaypoints.forEach(p => {
        p.x = (p.x - centerX) * scale;
        p.y = (p.y - centerY) * scale;
        p.z = (p.z - centerZ) * scale;
    });

    // Build Graph (Connect nearest neighbors)
    const waypointsStruct: Waypoint[] = generatedWaypoints.map(p => ({
        position: p,
        neighbors: []
    }));

    const K_NEIGHBORS = 8;
    
    waypointsStruct.forEach((wp, i) => {
        const distances: { idx: number, dist: number }[] = [];
        for (let j = 0; j < waypointsStruct.length; j++) {
            if (i === j) continue;
            const d = wp.position.distanceToSquared(waypointsStruct[j].position);
            distances.push({ idx: j, dist: d });
        }
        distances.sort((a, b) => a.dist - b.dist);
        wp.neighbors = distances.slice(0, K_NEIGHBORS).map(d => d.idx);
    });

    // Initialize Particles
    const PARTICLE_COUNT = 2000;
    const parts: Particle[] = [];
    const pos = new Float32Array(PARTICLE_COUNT * 3);
    const sizes = new Float32Array(PARTICLE_COUNT);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
        const startIdx = Math.floor(Math.random() * waypointsStruct.length);
        const startWp = waypointsStruct[startIdx];
        const targetIdx = startWp.neighbors.length > 0 
            ? startWp.neighbors[Math.floor(Math.random() * startWp.neighbors.length)]
            : startIdx;
        
        parts.push({
            currentWaypoint: startIdx,
            targetWaypoint: targetIdx,
            progress: Math.random(),
            speed: 0.001 + Math.random() * 0.002,
            dx: 0,
            dy: 0,
            dz: 0
        });

        const p1 = waypointsStruct[startIdx].position;
        const p2 = waypointsStruct[targetIdx].position;
        const currentPos = new THREE.Vector3().lerpVectors(p1, p2, parts[i].progress);
        
        pos[i*3] = currentPos.x;
        pos[i*3+1] = currentPos.y;
        pos[i*3+2] = currentPos.z;

        // Random size between 0.5 and 1.5 of base size
        sizes[i] = 0.5 + Math.random() * 1.0;
    }

    return { positions: pos, particles: parts, waypoints: waypointsStruct, sizes };
  }, [obj]);

  // Custom shader for particles
  const shaderArgs = useMemo(
    () => ({
      uniforms: {
        uColor: { value: new THREE.Color(PRIMARY_COLOR) },
        uBaseSize: { value: 0.15 },
        uGlowStrength: { value: 3.0 }, // Adjust this value to change glow strength
      },
      vertexShader: `
        uniform float uBaseSize;
        attribute float aSize;
        
        void main() {
          vec3 pos = position;
          vec4 mvPosition = viewMatrix * modelMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = uBaseSize * aSize * (300.0 / -mvPosition.z);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uGlowStrength;
        
        void main() {
          vec2 center = gl_PointCoord - 0.5;
          float dist = length(center);
          if (dist > 0.5) discard;
          
          // Create a soft glow effect
          // Distance from center (0.0) to edge (0.5) mapped to 1.0 -> 0.0
          float strength = 1.0 - (dist * 2.0);
          
          // Sharpen the falloff so it's not just a linear blur
          strength = pow(strength, 1.5);
          
          // Apply the glow strength multiplier
          strength *= uGlowStrength;
          
          gl_FragColor = vec4(uColor, strength);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    }),
    []
  );

  const currentScale = useRef(0);

  // Temp vectors for mouse interaction
  const tempVec = useMemo(() => new THREE.Vector3(), []);
  const tempClosest = useMemo(() => new THREE.Vector3(), []);
  const tempDir = useMemo(() => new THREE.Vector3(), []);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const inverseMatrix = useMemo(() => new THREE.Matrix4(), []);

  useFrame((state, delta) => {
    if (pointsRef.current && geometryRef.current && particles.length > 0) {
      // Rotate horizontally (Y axis)
      pointsRef.current.rotation.y += 0.001;
      pointsRef.current.updateMatrixWorld();

      // Popup animation
      currentScale.current = THREE.MathUtils.lerp(currentScale.current, 1, delta * 3);
      pointsRef.current.scale.setScalar(currentScale.current);

      // Mouse Interaction Setup
      raycaster.setFromCamera(state.pointer, state.camera);
      const ray = raycaster.ray;
      
      // Transform ray to local space
      inverseMatrix.copy(pointsRef.current.matrixWorld).invert();
      ray.applyMatrix4(inverseMatrix);

      const REPULSION_RADIUS = 0.5;
      const REPULSION_STRENGTH = 0.1; // Weak interaction
      const radiusSq = REPULSION_RADIUS * REPULSION_RADIUS;

      const posAttr = geometryRef.current.attributes.position;
      const array = posAttr.array as Float32Array;

      for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          p.progress += p.speed;

          if (p.progress >= 1.0) {
              p.progress = 0;
              p.currentWaypoint = p.targetWaypoint;
              const currentWp = waypoints[p.currentWaypoint];
              if (currentWp.neighbors.length > 0) {
                  p.targetWaypoint = currentWp.neighbors[Math.floor(Math.random() * currentWp.neighbors.length)];
              }
          }

          const p1 = waypoints[p.currentWaypoint].position;
          const p2 = waypoints[p.targetWaypoint].position;

          // Lerp for network movement
          const baseX = p1.x + (p2.x - p1.x) * p.progress;
          const baseY = p1.y + (p2.y - p1.y) * p.progress;
          const baseZ = p1.z + (p2.z - p1.z) * p.progress;

          // Mouse Repulsion Calculation
          tempVec.set(baseX, baseY, baseZ);
          const distSq = ray.distanceSqToPoint(tempVec);

          let targetDx = 0;
          let targetDy = 0;
          let targetDz = 0;

          if (distSq < radiusSq) {
              ray.closestPointToPoint(tempVec, tempClosest);
              const dist = Math.sqrt(distSq);
              const force = (REPULSION_RADIUS - dist) / REPULSION_RADIUS; // 0 to 1
              
              // Direction from closest point on ray to particle
              tempDir.copy(tempVec).sub(tempClosest).normalize();
              
              // Calculate target displacement
              targetDx = tempDir.x * force * REPULSION_STRENGTH;
              targetDy = tempDir.y * force * REPULSION_STRENGTH;
              targetDz = tempDir.z * force * REPULSION_STRENGTH;
          }

          // Smoothly interpolate current displacement towards target displacement
          p.dx += (targetDx - p.dx) * 0.1;
          p.dy += (targetDy - p.dy) * 0.1;
          p.dz += (targetDz - p.dz) * 0.1;

          array[i*3] = baseX + p.dx;
          array[i*3+1] = baseY + p.dy;
          array[i*3+2] = baseZ + p.dz;
      }

      posAttr.needsUpdate = true;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry ref={geometryRef}>
        <bufferAttribute
          attach="attributes-position"
          count={particles.length}
          array={positions}
          itemSize={3}
          args={[positions, 3]}
        />
        <bufferAttribute
          attach="attributes-aSize"
          count={particles.length}
          array={sizes}
          itemSize={1}
          args={[sizes, 1]}
        />
      </bufferGeometry>
      <shaderMaterial args={[shaderArgs]} />
    </points>
  );
};

const HeroParticles = () => {
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <Canvas 
        camera={{ position: [0, 0, 6], fov: 45 }}
        style={{ pointerEvents: 'none' }}
      >
        <Suspense fallback={null}>
            <group position={[0,0,0]}>
                <ParticleObject />
            </group>
        </Suspense>
      </Canvas>
      {/* CSS Blur overlay for extra "blur" effect if needed, or rely on the mesh */}
      <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${PRIMARY_COLOR} 0%, transparent 70%)`,
          filter: 'blur(60px)',
          opacity: 0.15,
          pointerEvents: 'none',
          zIndex: -1
      }}>
        {/* Noise overlay inside */}
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            opacity: 0.4,
            mixBlendMode: 'overlay',
            borderRadius: '50%'
        }} />
      </div>
    </div>
  );
};

export default HeroParticles;
